import classNames from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';
import React from 'react';
import { Swiper as SwiperInstance } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import Icon from '@/components/atoms/icon/icon';
import Image from '@/components/atoms/image/image';
import RichText from '@/components/atoms/rich-text/rich-text';
import Typography from '@/components/atoms/typography/typography';
import Button from '@/components/molecules/button/button';
import IconLabel from '@/components/molecules/icon-label/icon-label';
import Tooltip from '@/components/molecules/tooltip/tooltip';
import Lightbox from '@/components/utilities/lightbox/lightbox';
import { useI18n } from '@/libraries/contexts/i18n-context';
import useCurrencyFormatter from '@/libraries/formatters/use-currency-formatter';
import styles from './extra-card-modal.module.scss';

export interface ExtraCardModalProps {
  /** The class name to apply */
  className?: string;
  /** The description of the card */
  description?: Content.Components['ExtraItemDto']['description'];
  /** The excluded features */
  excludedFeatures?: Content.Components['ExtraItemDto']['excludedFeatures'];
  /** The excluded feature heading */
  excludedText?: Content.Components['ExtraItemDto']['excludedText'];
  /** The headline of the card */
  headline?: Content.Components['ExtraItemDto']['headline'];
  /** List of features to show in the card */
  highlightedFeatures?: Content.Components['ExtraItemDto']['highlightedFeatures'];
  /** The images to show in the card */
  images: Content.Components['ExtraItemDto']['images'];
  /** The included features */
  includedFeatures?: Content.Components['ExtraItemDto']['includedFeatures'];
  /** The included feature heading */
  includedText?: Content.Components['ExtraItemDto']['includedText'];
  /** Indicates whether the modal is open. */
  isOpen?: boolean;
  /** The link in the modal */
  link?: Content.Components['ExtraItemDto']['link'];
  /** The callback to handle the close event. */
  onClose: () => void;
  /** The description displayed under the included/excluded items */
  overlayDescription?: Content.Components['ExtraItemDto']['overlayDescription'];
  /** The headline displayed under the included/excluded items */
  overlayHeadline?: Content.Components['ExtraItemDto']['overlayHeadline'];
  /** The price items list */
  priceItems: Content.Components['ExtraItemDto']['priceItems'];
}

const ExtraCardModal = ({
  className,
  description,
  excludedFeatures = [],
  excludedText,
  headline,
  highlightedFeatures = [],
  images = [],
  includedFeatures = [],
  includedText,
  isOpen = false,
  link,
  onClose,
  overlayDescription,
  overlayHeadline,
  priceItems = [],
}: ExtraCardModalProps) => {
  const { t } = useI18n();
  const currencyFormatter = useCurrencyFormatter();
  const swiperRef = useRef<SwiperInstance>();
  const [activeItemIndex, setActiveItemIndex] = useState(0);

  const handleNext = useCallback(() => {
    if (!swiperRef.current) return;

    swiperRef.current.slideNext();
  }, [swiperRef]);

  const handlePrevious = useCallback(() => {
    if (!swiperRef.current) return;

    swiperRef.current.slidePrev();
  }, [swiperRef]);

  useEffect(() => {
    if (!window) return;

    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        handlePrevious();
      }

      if (event.key === 'ArrowRight') {
        handleNext();
      }

      if (event.key === 'Escape') {
        onClose && onClose();
      }
    };

    window.addEventListener('keydown', handleKeydown);

    return () => {
      window.removeEventListener('keydown', handleKeydown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <Lightbox
      className={classNames(styles.component, className)}
      isOpen={isOpen}
      onClose={onClose}
      wrapperClassName={styles.lightboxWrapper}
    >
      <div className={styles.lightboxInnerWrapper}>
        <div className={styles.contentWrapper}>
          <div className={styles.topSection}>
            <Typography
              className={styles.headline}
              tag="h3"
              tagStyle="headingMedium"
            >
              {headline}
            </Typography>

            {description && (
              <RichText className={styles.description} text={description} />
            )}

            {highlightedFeatures && highlightedFeatures.length > 0 && (
              <div className={styles.highlightedFeatures}>
                {highlightedFeatures
                  .slice(0, 3)
                  .map((feature, featureIndex) => (
                    <div key={featureIndex}>
                      <Typography tag="label" tagStyle="bodyMediumBold">
                        {feature.headline}
                      </Typography>

                      <IconLabel
                        icon={feature.icon as IconName}
                        text={feature.text || ''}
                      />
                    </div>
                  ))}
              </div>
            )}

            {includedText && includedFeatures.length > 0 && (
              <React.Fragment>
                <Typography className={styles.item} tagStyle="bodyMediumBold">
                  {includedText}
                </Typography>

                <div className={styles.featuresWrapper}>
                  {includedFeatures.map((feature, featureIndex) => (
                    <Typography
                      key={featureIndex}
                      className={styles.item}
                      tagStyle="bodyMedium"
                    >
                      <Icon
                        className={classNames(styles.icon, styles.isIncluded)}
                        name="CheckMark"
                      />

                      {feature.text}
                    </Typography>
                  ))}
                </div>
              </React.Fragment>
            )}

            {excludedText && excludedFeatures.length > 0 && (
              <React.Fragment>
                <Typography tagStyle="bodyMediumBold">
                  {excludedText}
                </Typography>

                <div className={styles.featuresWrapper}>
                  {excludedFeatures.map((feature, featureIndex) => (
                    <Typography
                      key={featureIndex}
                      className={styles.item}
                      tagStyle="bodyMedium"
                    >
                      <Icon
                        className={classNames(styles.icon, styles.isExcluded)}
                        name="Minus"
                      />

                      {feature.text}
                    </Typography>
                  ))}
                </div>
              </React.Fragment>
            )}

            {overlayHeadline && (
              <Typography className={styles.headline} tagStyle="bodyMediumBold">
                {overlayHeadline}
              </Typography>
            )}

            {overlayDescription && (
              <Typography
                className={styles.description}
                tag="p"
                tagStyle="bodyMedium"
              >
                {overlayDescription}
              </Typography>
            )}
          </div>

          <div className={styles.bottomSection}>
            {priceItems && priceItems.length > 0 && (
              <div
                className={classNames(styles.priceItemsWrapper, {
                  [styles.hasLink]: !!link,
                })}
              >
                {priceItems
                  .filter(item => item.price !== 0)
                  .map((priceItem, priceItemIndex) => (
                    <div key={priceItemIndex} className={styles.priceItem}>
                      <div className={styles.priceItemLabel}>
                        <Typography
                          className={styles.item}
                          tagStyle="bodyMedium"
                        >
                          {priceItem.label}
                        </Typography>

                        {priceItem.tooltip && (
                          <Tooltip
                            className={styles.tooltip}
                            text={priceItem.tooltip as string}
                          />
                        )}
                      </div>

                      {priceItem.price && (
                        <Typography
                          className={styles.item}
                          tagStyle="priceLarge"
                        >
                          {currencyFormatter(priceItem.price)}
                        </Typography>
                      )}
                    </div>
                  ))}
              </div>
            )}

            {link && (
              <Button
                className={styles.cta}
                text={link?.text}
                url={link?.url}
                variant="primary"
              />
            )}
          </div>
        </div>

        {images.length > 0 && (
          <div className={styles.imageWrapper}>
            {images.length > 1 && (
              <div className={styles.counter}>
                <Typography tagStyle="labelSmall">{`${activeItemIndex + 1} / ${images.length}`}</Typography>
              </div>
            )}

            <Swiper
              className={classNames(styles.swiper, {
                [styles.hasControls]: images.length > 1,
              })}
              initialSlide={0}
              loop={images.length > 1}
              onActiveIndexChange={({ realIndex }) => {
                setActiveItemIndex(realIndex);
              }}
              onSwiper={swiper => {
                swiperRef.current = swiper;
              }}
              slidesPerView={1}
              wrapperClass={styles.wrapper}
            >
              {images.map((image, slideIndex) => (
                <SwiperSlide key={slideIndex} className={styles.slide}>
                  <Image
                    alt={image.image.altText}
                    aspectRatio={'sixteenByNine'}
                    className={styles.image}
                    fill={true}
                    focalPoint={image.image.focalPoint}
                    src={image.image.url}
                    srcSet={{
                      desktop: {
                        width: 520,
                      },
                      tablet: {
                        width: 830,
                      },
                    }}
                    unoptimized
                    width={343}
                  />
                </SwiperSlide>
              ))}

              {images.length > 1 && (
                <div className={styles.buttonWrapper}>
                  <Button
                    aria-label={t('images.common.words.previous')}
                    icon="ChevronLeft"
                    iconSize={16}
                    onClick={handlePrevious}
                    variant="tertiary"
                  />

                  <Button
                    aria-label={t('items.common.words.next')}
                    icon="ChevronRight"
                    iconSize={16}
                    onClick={handleNext}
                    variant="tertiary"
                  />
                </div>
              )}
            </Swiper>

            {images[activeItemIndex]?.caption && (
              <Typography tag="label" tagStyle="labelMedium">
                {images[activeItemIndex].caption}
              </Typography>
            )}
          </div>
        )}
      </div>
    </Lightbox>
  );
};

ExtraCardModal.displayName = 'ExtraCardModal';

export default ExtraCardModal;
